"use client";

import { Button } from "../../../../shadcn/ui/button";
import { Input } from "../../../../shadcn/ui/input";
import { Label } from "../../../../shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../shadcn/ui/select";
import { MapPin, HelpCircle, Plus, Layers } from "lucide-react";
import type { ListingData } from "./index";
import { useState, useEffect, useCallback, useRef } from "react";
import { buildingAddressService } from "../../../../services/buildingAddressService";
import InlineBuildingAddressForm from "../../../host-experience/host-add-space/components/InlineBuildingAddressForm";
import ManageFloorsDialog from "../../../host-experience/host-add-space/components/ManageFloorsDialog";
import SearchableBuildingAddress from "../../../host-experience/host-add-space/components/SearchableBuildingAddress";
// @ts-ignore
import { Map, AdvancedMarker } from "@vis.gl/react-google-maps";
import MapErrorBoundary from "../../../../components/MapErrorBoundary";
import { getCurrentUserOrganizationId } from "../../host-clients/utils/hostClientsUtils";
import logger from "../../../../utils/logger";


interface InstanceDetailsCardProps {
  data: ListingData;
  updateData: (data: Partial<ListingData>) => void;
}

export default function InstanceDetailsCard({ data, updateData }: InstanceDetailsCardProps) {
  const [buildingFloors, setBuildingFloors] = useState<{ [key: number]: Array<any> }>({});
  const [loadingFloors, setLoadingFloors] = useState<{ [key: number]: boolean }>({});
  const [buildingData, setBuildingData] = useState<{ [key: number]: any }>({});
  const [mapError, setMapError] = useState(false);
  const [initialFloorId, setInitialFloorId] = useState<number | undefined>(undefined);

  // Store initial floorId from edit mode - this runs once when component mounts with edit data
  useEffect(() => {
    if (data.floorId != null && initialFloorId === undefined) {
      setInitialFloorId(data.floorId);
    }
  }, [data.floorId, initialFloorId]);

  const handleAddressCreated = (newAddress: any) => {
    if (!newAddress || !newAddress.id) {
      logger.error("Invalid new address received:", newAddress);
      return;
    }

    logger.log("🏗️ New building address created:", newAddress);

    // Store building data to avoid re-fetching
    setBuildingData(prev => ({
      ...prev,
      [newAddress.id]: newAddress,
    }));

    // Store floors if provided
    if (Array.isArray(newAddress.floors)) {
      setBuildingFloors(prev => ({
        ...prev,
        [newAddress.id]: newAddress.floors,
      }));
    }

    // Auto-select the newly created address and update coordinates
    updateData({
      buildingAddressId: newAddress.id,
      latitude: newAddress.latitude,
      longitude: newAddress.longitude,
    });
  };

  // Pre-populate floors from API response if available
  useEffect(() => {
    if (data.buildingAddressId && data.initialFloors?.length && !buildingFloors[data.buildingAddressId]) {
      setBuildingFloors(prev => ({
        ...prev,
        [data.buildingAddressId!]: data.initialFloors!,
      }));
      setBuildingData(prev => ({
        ...prev,
        [data.buildingAddressId!]: {
          id: data.buildingAddressId,
          floors: data.initialFloors,
          latitude: data.latitude,
          longitude: data.longitude,
        },
      }));
    }
  }, [data.buildingAddressId, data.initialFloors, data.latitude, data.longitude, buildingFloors]);

  // Fetch floors and coordinates for a specific building
  // IMPORTANT: forceFetch is used to bypass cache when we need to ensure floors are loaded (e.g., in edit mode)
  const fetchFloorsForBuilding = useCallback(
    async (buildingId: number, forceFetch: boolean = false) => {
      if (!buildingId) return;

      // Skip if we already have both floors and building data (unless force fetching)
      if (!forceFetch && buildingFloors[buildingId] && buildingData[buildingId]) return;

      try {
        setLoadingFloors(prev => ({ ...prev, [buildingId]: true }));
        const response = await buildingAddressService.getBuildingAddressById(buildingId);

        if (response.status === 200 && response.data) {
          const building = response.data;

          // Store the full building data
          setBuildingData(prev => ({ ...prev, [buildingId]: building }));

          const floors = Array.isArray(building.floors) ? building.floors : [];

          // Remove duplicate floors based on id only (not floorNumber to prevent removing legitimate floors)
          const uniqueFloors = floors.filter(
            (floor: any, index: number, self: any[]) => index === self.findIndex((f: any) => f.id === floor.id)
          );

          setBuildingFloors(prev => ({ ...prev, [buildingId]: uniqueFloors }));

          // Update form data with coordinates from building address
          if (building.latitude != null && building.longitude != null) {
            updateData({
              latitude: building.latitude,
              longitude: building.longitude,
            });
            logger.log("📍 Updated coordinates from building:", {
              latitude: building.latitude,
              longitude: building.longitude,
            });
          }
        }
      } catch (error) {
        logger.error("Error fetching floors:", error);
        setBuildingFloors(prev => ({ ...prev, [buildingId]: [] }));
      } finally {
        setLoadingFloors(prev => ({ ...prev, [buildingId]: false }));
      }
    },
    [buildingFloors, buildingData, updateData]
  );

  // Fetch floors when building changes
  useEffect(() => {
    if (data.buildingAddressId) {
      // Check if we need to force fetch (when we have an initialFloorId but no floors loaded yet)
      const needForceFetch = initialFloorId != null && !buildingFloors[data.buildingAddressId];
      fetchFloorsForBuilding(data.buildingAddressId, needForceFetch);
    }
  }, [data.buildingAddressId, fetchFloorsForBuilding, initialFloorId, buildingFloors]);

  const currentFloors = data.buildingAddressId ? buildingFloors[data.buildingAddressId] || [] : [];
  const currentOrgId = getCurrentUserOrganizationId();
  const isLoadingFloors = data.buildingAddressId ? loadingFloors[data.buildingAddressId] : false;

  // Get current building address details for display
  const currentBuilding = data.buildingAddressId ? buildingData[data.buildingAddressId] : null;

  // Get coordinates for map - use buildingData if available, otherwise fall back to form data
  const mapLatitude = buildingData[data.buildingAddressId!]?.latitude ?? data.latitude ?? -1.2921;
  const mapLongitude = buildingData[data.buildingAddressId!]?.longitude ?? data.longitude ?? 36.8219;
  const hasValidCoordinates = mapLatitude != null && mapLongitude != null;

  const handleMapError = () => {
    setMapError(true);
  };

  // Determine if the floor select should show the value
  // Show value if: floorId exists (it will be matched against currentFloors when they load)
  const shouldShowFloorValue = data.floorId != null;

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="mb-4 flex items-center gap-2">
        <MapPin className="h-5 w-5" />
        <h3 className="font-semibold">Location & Instance Details</h3>
      </div>

      <div className="space-y-4">
        {/* Location/Instance Name (used for instances[0].location.name) */}
        <div className="space-y-2">
          <Label htmlFor="location" className="flex items-center" {...({} as any)}>
            Event Name <HelpCircle className="w-4 h-4 ml-1 text-muted-foreground" />
          </Label>
          <Input
            id="location"
            placeholder="e.g., Beach Yoga Session or Main Event Hall"
            value={data.location}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateData({ location: e.target.value })}
            {...({} as any)}
          />
        </div>

        {/* Building Address - Searchable Dropdown */}
        <div className="space-y-2">
          <Label htmlFor="buildingAddressId" className="flex items-center" {...({} as any)}>
            Building Address <HelpCircle className="w-4 h-4 ml-1 text-muted-foreground" />
          </Label>
          <div className="flex gap-2">
            <SearchableBuildingAddress
              value={data.buildingAddressId}
              onChange={(buildingId: number) => {
                updateData({ buildingAddressId: buildingId });
                fetchFloorsForBuilding(buildingId);
              }}
              onBuildingSelect={(building: any) => {
                // Store building data and floors when selected
                setBuildingData(prev => ({
                  ...prev,
                  [building.id]: building,
                }));
                if (Array.isArray(building.floors)) {
                  setBuildingFloors(prev => ({
                    ...prev,
                    [building.id]: building.floors,
                  }));
                }
                // Update coordinates
                if (building.latitude && building.longitude) {
                  updateData({
                    latitude: building.latitude,
                    longitude: building.longitude,
                  });
                }
              }}
              placeholder="Search building addresses..."
              className="flex-1 min-w-0"
            />
            <div className="flex-shrink-0">
              <InlineBuildingAddressForm
                onAddressCreated={handleAddressCreated}
                triggerButton={
                  <Button variant="outline" size="icon" type="button" {...({} as any)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                }
              />
            </div>
            <div className="flex-shrink-0">
              <ManageFloorsDialog
                buildingAddressId={data.buildingAddressId || null}
                isReadOnly={false}
                triggerButton={
                  <Button variant="outline" size="sm" type="button" {...({} as any)}>
                    <Layers className="w-4 h-4 mr-2" />
                    Manage Floors
                  </Button>
                }
                onUpdated={(updatedBuilding: any) => {
                  // Refresh floors cache when floors are updated
                  if (updatedBuilding && updatedBuilding.id) {
                    setBuildingFloors(prev => ({
                      ...prev,
                      [updatedBuilding.id]: updatedBuilding.floors || [],
                    }));
                  }
                }}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Building where this play service will take place</p>
        </div>

        {/* Map showing selected building location */}
        {data.buildingAddressId && hasValidCoordinates && (
          <div className="space-y-2">
            <Label className="flex items-center" {...({} as any)}>
              Location Preview
            </Label>
            <div className="h-[200px] w-full rounded-lg overflow-hidden border border-gray-200 relative">
              {!mapError ? (
                <MapErrorBoundary onError={handleMapError}>
                  <Map
                    mapId="DEMO_MAP_ID"
                    center={{ lat: Number(mapLatitude) || -1.2921, lng: Number(mapLongitude) || 36.8219 }}
                    zoom={15}
                    disableDefaultUI={true}
                    zoomControl={true}
                    styles={[{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }]}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <AdvancedMarker position={{ lat: Number(mapLatitude), lng: Number(mapLongitude) }}>
                      <div className="relative">
                        <div className="absolute -translate-x-1/2 -translate-y-full">
                          <MapPin className="w-8 h-8 text-red-600 fill-red-100 drop-shadow-lg" />
                        </div>
                      </div>
                    </AdvancedMarker>
                  </Map>
                </MapErrorBoundary>
              ) : (
                <div className="h-full w-full bg-gray-100 flex items-center justify-center">
                  <div className="text-center text-sm text-muted-foreground">
                    <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    Map unavailable
                  </div>
                </div>
              )}
            </div>
            {currentBuilding && (
              <p className="text-xs text-muted-foreground">
                📍 {currentBuilding.buildingName} - {currentBuilding.streetAddress}, {currentBuilding.city}
              </p>
            )}
          </div>
        )}

        {/* Floor Selection */}
        <div className="space-y-2">
          <Label htmlFor="floorId" className="flex items-center" {...({} as any)}>
            Floor Level <HelpCircle className="w-4 h-4 ml-1 text-muted-foreground" />
          </Label>
          <div className="flex gap-2">
            <Select
              value={shouldShowFloorValue ? String(data.floorId) : undefined}
              onValueChange={value => updateData({ floorId: parseInt(value) })}
              disabled={!data.buildingAddressId || isLoadingFloors}
            >
              <SelectTrigger id="floorId" {...({} as any)}>
                <SelectValue
                  placeholder={
                    !data.buildingAddressId
                      ? "Select building first"
                      : isLoadingFloors
                        ? "Loading floors..."
                        : "Select floor"
                  }
                  {...({} as any)}
                />
              </SelectTrigger>
              <SelectContent {...({} as any)}>
                {currentFloors.length > 0 ? (
                  currentFloors.map((floor: any) => {
                    const displayLabel = floor.label || `Floor ${floor.floorNumber ?? floor.id}`;
                    const isFloorLocked = !currentOrgId || floor.organizationId !== currentOrgId;
                    return (
                      <SelectItem key={floor.id} value={String(floor.id)} disabled={isFloorLocked} {...({} as any)}>
                        {displayLabel}
                        {isFloorLocked ? " (locked)" : ""}
                      </SelectItem>
                    );
                  })
                ) : (
                  <SelectItem value="no-floors" disabled {...({} as any)}>
                    {!data.buildingAddressId
                      ? "Select a building first"
                      : isLoadingFloors
                        ? "Loading floors..."
                        : "No floors found. Use Manage Floors to add some."}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">Floor level for this play service instance</p>
        </div>
      </div>
    </div>
  );
}
